import type { FormEvent } from 'react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { filter, includes, range } from 'lodash-es';

import { Box, Divider, FormField, Label, Paragraph, TextButton } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import PersonInformation from './PeopleInformation/PersonInformation';
import YesAndNo from '../../Widgets/YesAndNo';
import { PeopleSection } from '../../../models/index';
import Field from '../../../models/Field';
import { LANDLORD_INFORMATION, PERSON } from '../../../constants/index';
import { PERSON_TYPE } from '../../../constants/stringConstants';

type Props = {
  children?: React.ReactNode;
  isOptional: boolean;
  initialPeople: number;
  maximumPeople?: number;
  personType: PERSON_TYPE;
  setSectionValues: (keyValuePairs: object) => void;
  setSection: (section: PeopleSection) => void;
  section: PeopleSection;
  addressLabel?: string;
  label?: string;
  description?: React.ReactNode;
  yesAndNoText?: string | React.ReactNode;
  className?: string;
  initialEmail?: string;
  initialFirstName?: string;
  initialLastName?: string;
};

PeopleInformation.defaultProps = {
  initialPeople: 1,
  isOptional: true,
};

export default function PeopleInformation({
  addressLabel,
  children,
  className,
  initialPeople,
  isOptional,
  maximumPeople,
  personType,
  section,
  setSection,
  setSectionValues,
  label,
  description,
  yesAndNoText,
  initialEmail,
  initialFirstName,
  initialLastName,
}: Props): JSX.Element {
  const [hasClicked, setHasClicked] = useState(false);
  const [yesEnabledAfterClick, setYesEnabledAfterClick] = useState(null);

  useEffect(() => {
    if (!(section instanceof PeopleSection)) {
      const peopleSection = new PeopleSection((section as PeopleSection).initialParams, personType);
      const landlordFirstNameValue = (section as PeopleSection).getFieldValue(
        LANDLORD_INFORMATION.FIELD_NAME.FIRST_NAME,
      );
      const landlordLastNameValue = (section as PeopleSection).getFieldValue(LANDLORD_INFORMATION.FIELD_NAME.LAST_NAME);

      // Use a for loop with a max iterations guard to prevent infinite loops
      // when indexedFieldTypes is empty (e.g., in Storybook with empty fields)
      const currentCount = peopleSection.getTotalIndexes() ?? 0;
      const iterationsNeeded = initialPeople - currentCount;
      for (let i = 0; i < iterationsNeeded; i++) {
        peopleSection.addOneIndex();
      }

      if (initialEmail) {
        peopleSection.setFieldValue(LANDLORD_INFORMATION.FIELD_NAME.EMAIL, initialEmail);
      }
      if (landlordFirstNameValue === '' && initialFirstName) {
        peopleSection.setFieldValue(LANDLORD_INFORMATION.FIELD_NAME.FIRST_NAME, initialFirstName);
      }
      if (landlordLastNameValue === '' && initialLastName) {
        peopleSection.setFieldValue(LANDLORD_INFORMATION.FIELD_NAME.LAST_NAME, initialLastName);
      }
      setSection(peopleSection);
    }
  }, [section, personType, initialPeople, initialEmail, initialFirstName, initialLastName, setSection]);

  function isEmailUnique(index, value) {
    const { fieldMappings } = section as PeopleSection;

    const isEmailDuplicate = includes(
      filter(
        fieldMappings,
        (field) =>
          field.fieldName.includes(PERSON.FIELD_NAME.EMAIL_ADDRESS) && !field.fieldName.includes(index.toString()),
      ).map((field) => field.getJsonValue().toLowerCase()),
      value.toLowerCase(),
    );

    const personTypeNameMap = {
      [PERSON_TYPE.COLANDLORD]: 'landlords',
      [PERSON_TYPE.LANDLORD]: 'landlords',
      [PERSON_TYPE.TENANT]: 'tenants',
      [PERSON_TYPE.GUARANTOR]: 'guarantor',
      [PERSON_TYPE.PROPERTY_MANAGER]: 'property managers',
    };

    if (isEmailDuplicate) {
      section
        .getField(`${personType}${index}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`)
        .setErrorMessage(`Emails for ${personTypeNameMap[personType]} must be unique`);
    }

    return !isEmailDuplicate;
  }

  function addOneIndex(): void {
    setHasClicked(true);
    setYesEnabledAfterClick(true);

    const updatedSection = (section as PeopleSection).clone() as PeopleSection;
    updatedSection.addOneIndex();
    setSection(updatedSection);
  }

  function onFieldChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [event.currentTarget.dataset.fieldName]: event.currentTarget.value,
    });
  }

  function onRemovePerson(event: FormEvent<HTMLElement>): void {
    const { index } = event.currentTarget.dataset;

    const updatedSection = (section as PeopleSection).clone() as PeopleSection;
    updatedSection.removeOneIndex(Number(index));
    setSection(updatedSection);
  }

  function onNoClick(): void {
    setHasClicked(true);
    setYesEnabledAfterClick(false);

    const updatedSection = (section as PeopleSection).clone() as PeopleSection;
    updatedSection.removeAllIndexes();
    setSection(updatedSection);
  }

  function renderPerson(index: number): React.ReactNode {
    const isEnabled = section.getFieldValue(Field.getIndexedFieldName(`${personType}_isEnabled`, index));

    if (!isEnabled) {
      return null;
    }

    const firstNameField = (section as PeopleSection).getIndexedFieldGroup(
      `${personType}_${PERSON.FIELD_NAME.FIRST_NAME}`,
    )[index];
    const lastNameField = (section as PeopleSection).getIndexedFieldGroup(
      `${personType}_${PERSON.FIELD_NAME.LAST_NAME}`,
    )[index];
    const emailField = (section as PeopleSection).getIndexedFieldGroup(
      `${personType}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`,
    )[index];

    const mailingAddressFields = (section as PeopleSection).getIndexedFieldGroup(
      `${personType}_${PERSON.FIELD_NAME.MAILING_ADDRESS}`,
    );
    const mailingAddressField = mailingAddressFields
      ? (section as PeopleSection).getIndexedFieldGroup(`${personType}_${PERSON.FIELD_NAME.MAILING_ADDRESS}`)[index]
      : null;

    const phoneNumberFields = (section as PeopleSection).getIndexedFieldGroup(
      `${personType}_${PERSON.FIELD_NAME.PHONE_NUMBER}`,
    );
    const phoneNumberField = phoneNumberFields
      ? (section as PeopleSection).getIndexedFieldGroup(`${personType}_${PERSON.FIELD_NAME.PHONE_NUMBER}`)[index]
      : null;

    section.setCustomFieldValidator(
      `${personType}${index}_${PERSON.FIELD_NAME.EMAIL_ADDRESS}`,
      isEmailUnique.bind(null, index),
    );

    /*
    *Note that enteredValues are being passed in separately from their respective fields.
    This is done so that the PersonInformation can be implemented as a PureComponent. Since
    the fields are object references that don't change between updates, updating
    them does not trigger updates since the references remain the same.

    Passing the enteredValues separately triggers the updates.
    */
    return (
      <PersonInformation
        index={index}
        onFieldChange={onFieldChange}
        onRemovePerson={onRemovePerson}
        firstNameField={firstNameField}
        firstNameValue={firstNameField.enteredValue}
        lastNameField={lastNameField}
        lastNameValue={lastNameField.enteredValue}
        phoneNumberField={phoneNumberField}
        phoneNumberValue={phoneNumberField && phoneNumberField.enteredValue}
        emailField={emailField}
        emailValue={emailField.enteredValue}
        mailingAddressField={mailingAddressField}
        mailingAddressValue={mailingAddressField && mailingAddressField.enteredValue}
        personTypeLabel={(section as PeopleSection).personTypeLabel}
        addressLabel={addressLabel}
        className={className}
      />
    );
  }
  const maxPeople = maximumPeople || (section as PeopleSection).maxIndexedFieldsThatCanBeAdded;
  const totalPeople = (section as PeopleSection).getTotalIndexes();
  let isEnabled;

  if (section.getFieldValue(`${personType}0_isEnabled`)) {
    isEnabled = true;
  } else if (hasClicked) {
    isEnabled = yesEnabledAfterClick;
  } else {
    isEnabled = section.hasVisited ? false : null;
  }

  if (totalPeople == null) {
    // This condition is true on first render before the Section object
    // is transitioned to a PeopleSection object in the constructor.
    return null;
  }

  const yesNoControl = <YesAndNo yesHandler={addOneIndex} noHandler={onNoClick} yesActive={isEnabled} />;

  return (
    <>
      {isOptional &&
        (label ? (
          <FormField
            required
            css={{ marginBottom: isEnabled ? 'layout.tight' : 0 }}
            label={<Label>{label}</Label>}
            control={yesNoControl}
            description={description}
          />
        ) : (
          <Box css={{ marginBottom: isEnabled ? 'layout.tight' : 0 }}>{yesNoControl}</Box>
        ))}
      {yesAndNoText && isEnabled && <Paragraph css={{ marginBottom: 'layout.tighter' }}>{yesAndNoText}</Paragraph>}
      {isEnabled && (
        <>
          {range(0, totalPeople).map((index: number) => (
            <div data-test="person-container" key={index}>
              {index > 0 && <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />}
              {renderPerson(index)}
            </div>
          ))}
          {children}
        </>
      )}
      {maxPeople > 1 && (
        <Box css={{ marginTop: 'layout.tight' }}>
          {totalPeople < maxPeople ? (
            <TextButton data-test="add-another" onClick={addOneIndex} icon={<IconPlusFilled />}>
              Add another {(section as PeopleSection).personTypeLabel.toLowerCase()}
            </TextButton>
          ) : (
            maxPeople > 1 && (
              <Paragraph>
                <span data-test="maximum-reached">
                  You’ve reached {`${maxPeople} ${(section as PeopleSection).personTypeLabel.toLowerCase()}s`}, which is
                  the maximum allowed.
                </span>
              </Paragraph>
            )
          )}
        </Box>
      )}
    </>
  );
}
